import { normalizeCompareDateKey } from '../../../filters/lib/statisticsCompareConfig';
import { buildLineCompareAnchorFromOrderRange, buildTrendSyncPatchFromTopBar, resolveComparePeriodChipLabel } from '../../../filters/lib/statisticsComparePresets';
import { pickStatisticsScopeFields } from '../../../filters/lib/statisticsFilterParams';
import { resolveStatisticsCreatedDateRangeLabel } from '../../../filters/lib/statisticsFilterTags';
import { buildOrderDateRangeKey, normalizeFiltersForPendingCompare } from '../../../filters/lib/statisticsOrderDatePatch';

const scopeWithoutDatesAndCompareToggles = (scope = {}) => {
	const { startDate, endDate, completedStartDate, completedEndDate, compareYoy, compareMom, ...rest } = scope;
	return rest;
};

const resolveComparePeriodLabels = (periods = [], anchorPeriodAxis = null) =>
	periods
		.map((period) =>
			resolveComparePeriodChipLabel({
				startDate: period?.startDate,
				endDate: period?.endDate,
				label: period?.label,
				periodAxis: period?.periodAxis ?? anchorPeriodAxis,
				months: period?.months,
				quarters: period?.quarters,
			}),
		)
		.filter(Boolean);

const normalizePendingFilters = (filters = {}) => normalizeFiltersForPendingCompare(filters);

/** debounce 窗口内顶栏 draft 尚未写入 applied */
export const hasTopBarStatFiltersPending = (draft = {}, applied = {}) => {
	const normalizedDraft = normalizePendingFilters(draft);
	const normalizedApplied = normalizePendingFilters(applied);

	if (normalizedDraft.startDate !== normalizedApplied.startDate || normalizedDraft.endDate !== normalizedApplied.endDate) {
		return true;
	}

	const draftScopeRest = scopeWithoutDatesAndCompareToggles(pickStatisticsScopeFields(normalizedDraft));
	const appliedScopeRest = scopeWithoutDatesAndCompareToggles(pickStatisticsScopeFields(normalizedApplied));
	if (JSON.stringify(draftScopeRest) !== JSON.stringify(appliedScopeRest)) {
		return true;
	}

	return normalizedDraft.compareYoy !== normalizedApplied.compareYoy || normalizedDraft.compareMom !== normalizedApplied.compareMom;
};

/** debounce 窗口内折线对比 draft 尚未写入 applied */
export const hasCompareAnalysisFiltersPending = (draft = {}, applied = {}) => {
	const normalizedDraft = normalizePendingFilters(draft);
	const normalizedApplied = normalizePendingFilters(applied);

	if (normalizedDraft.startDate !== normalizedApplied.startDate || normalizedDraft.endDate !== normalizedApplied.endDate) {
		return false;
	}

	const draftScopeRest = scopeWithoutDatesAndCompareToggles(pickStatisticsScopeFields(normalizedDraft));
	const appliedScopeRest = scopeWithoutDatesAndCompareToggles(pickStatisticsScopeFields(normalizedApplied));
	if (JSON.stringify(draftScopeRest) !== JSON.stringify(appliedScopeRest)) {
		return false;
	}

	if (normalizedDraft.compareYoy !== normalizedApplied.compareYoy || normalizedDraft.compareMom !== normalizedApplied.compareMom) {
		return false;
	}

	if (JSON.stringify(normalizedDraft.customComparePeriods ?? []) !== JSON.stringify(normalizedApplied.customComparePeriods ?? [])) {
		return true;
	}

	const lineConfigKeys = ['trendMode', 'trendMonthlyYears', 'trendMonthlyYoy', 'trendAxisMode'];
	if (lineConfigKeys.some((key) => JSON.stringify(normalizedDraft[key]) !== JSON.stringify(normalizedApplied[key]))) {
		return true;
	}

	return JSON.stringify(normalizedDraft.trendAnchor) !== JSON.stringify(normalizedApplied.trendAnchor);
};

const COMPARE_ANALYSIS_PENDING_KINDS = new Set(['compare', 'anchor', 'lineConfig']);

const TREND_FIELD_KEYS = ['customComparePeriods', 'trendAnchor', 'trendMode', 'trendMonthlyYears', 'trendMonthlyYoy', 'trendAxisMode'];

const hasDraftLineCompareEdits = (draft = {}, applied = {}) => TREND_FIELD_KEYS.some((key) => JSON.stringify(draft[key] ?? null) !== JSON.stringify(applied[key] ?? null));

/** 已配置折线对比（基准 + 至少一段对比） */
export const hasActiveTrendConfig = (filters = {}) =>
	Boolean(filters?.trendAnchor?.startDate && filters?.trendAnchor?.endDate && ((filters?.customComparePeriods?.length ?? 0) > 0 || (filters?.trendMonthlyYears?.length ?? 0) > 0));

/**
 * 顶栏「查询」：日期未变、仅业务筛选/同比环比变更时，保留已生效对比分析并同步 scope，使折线图随查询刷新。
 */
export const mergeTopBarQueryWithDefaultLineCompare = (draft = {}, applied = {}, { anchorCustomized = false } = {}) => {
	if (!hasTopBarStatFiltersPending(draft, applied)) {
		return draft;
	}

	const datesUnchanged = buildOrderDateRangeKey(draft.startDate, draft.endDate) === buildOrderDateRangeKey(applied.startDate, applied.endDate);
	if (!datesUnchanged || !hasActiveTrendConfig(applied) || anchorCustomized) {
		return draft;
	}

	if (hasDraftLineCompareEdits(draft, applied)) {
		return {
			...draft,
			...buildTrendSyncPatchFromTopBar(draft),
		};
	}

	const merged = {
		...applied,
		...pickStatisticsScopeFields(draft),
		compareYoy: draft.compareYoy,
		compareMom: draft.compareMom,
	};

	return {
		...merged,
		...buildTrendSyncPatchFromTopBar(merged),
	};
};

/** 折线基准是否与顶栏下单日期同步（未自定义） */
export const isLineCompareAnchorSyncedToTopBar = (filters = {}) => {
	const anchor = filters?.trendAnchor;
	if (!anchor?.startDate || !anchor?.endDate) return true;
	const synced = buildLineCompareAnchorFromOrderRange(filters.startDate, filters.endDate);
	if (!synced) return false;
	return (
		normalizeCompareDateKey(anchor.startDate) === normalizeCompareDateKey(synced.startDate) && normalizeCompareDateKey(anchor.endDate) === normalizeCompareDateKey(synced.endDate)
	);
};

/** 待应用改动的可读片段（用于底部粘性应用条） */
export const summarizePrimaryPhaseStatPending = (draft = {}, applied = {}) => {
	const segments = [];

	if (draft.startDate !== applied.startDate || draft.endDate !== applied.endDate) {
		const label = resolveStatisticsCreatedDateRangeLabel(draft.startDate, draft.endDate, draft.trendAnchor);
		segments.push({ kind: 'period', text: `本期 ${label || '—'}` });
	}

	const draftScopeRest = scopeWithoutDatesAndCompareToggles(pickStatisticsScopeFields(draft));
	const appliedScopeRest = scopeWithoutDatesAndCompareToggles(pickStatisticsScopeFields(applied));
	if (JSON.stringify(draftScopeRest) !== JSON.stringify(appliedScopeRest)) {
		segments.push({ kind: 'filters', text: '筛选已修改' });
	}

	if (draft.compareYoy !== applied.compareYoy) {
		segments.push({ kind: 'yoy', text: draft.compareYoy !== false ? '同比开' : '同比关' });
	}

	if (draft.compareMom !== applied.compareMom) {
		segments.push({ kind: 'mom', text: draft.compareMom !== false ? '环比开' : '环比关' });
	}

	if (JSON.stringify(draft.customComparePeriods ?? []) !== JSON.stringify(applied.customComparePeriods ?? [])) {
		const labels = resolveComparePeriodLabels(draft.customComparePeriods, draft.trendAnchor?.periodAxis);
		if (labels.length > 0) {
			segments.push({ kind: 'compare', text: `对比 ${labels.join('、')}` });
		} else {
			segments.push({ kind: 'compare', text: '已清空对比段' });
		}
	}

	if (JSON.stringify(draft.trendAnchor) !== JSON.stringify(applied.trendAnchor)) {
		const anchor = draft.trendAnchor;
		if (anchor?.startDate && anchor?.endDate) {
			const label = resolveComparePeriodChipLabel({
				startDate: anchor.startDate,
				endDate: anchor.endDate,
				label: anchor.label,
				periodAxis: anchor.periodAxis,
				months: anchor.months,
				quarters: anchor.quarters,
			});
			segments.push({ kind: 'anchor', text: `自定义基准 ${label || '—'}` });
		} else {
			segments.push({ kind: 'anchor', text: '基准已重置' });
		}
	}

	const lineConfigKeys = ['trendMode', 'trendMonthlyYears', 'trendMonthlyYoy', 'trendAxisMode'];
	if (lineConfigKeys.some((key) => JSON.stringify(draft[key]) !== JSON.stringify(applied[key]))) {
		segments.push({ kind: 'lineConfig', text: '折线对比配置已修改' });
	}

	return segments;
};

/** 对比分析区底部条摘要（不含顶栏日期/筛选/同比环比） */
export const summarizeCompareAnalysisStatPending = (draft = {}, applied = {}) =>
	summarizePrimaryPhaseStatPending(draft, applied).filter((segment) => COMPARE_ANALYSIS_PENDING_KINDS.has(segment.kind));

export const formatPendingSummaryText = (segments = []) => {
	if (!segments.length) return '条件已修改';
	return segments.map((segment) => segment.text).join(' · ');
};
